import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import BatteryGauge from 'react-battery-gauge';
import { useMQTT } from './hooks/useMQTT';
import VirtualJoystick from './VirtualJoystick';
import SpeedSlider from './SpeedSlider';
import DPad from './DPad';
import CommandLog from './CommandLog';
import CameraView from './CameraView';
import s from '../../Assets/Styles/Joystick/RobotControl.module.css';

const DEADZONE = 0.14;

/*
 * deriveCmd — Joystick to Robot command
 *
 * Cmd (c): F=Forward  B=Backward  S=Stop/Turn-in-place   (NEVER L or R)
 * Turn(t): L=Left     R=Right     D=Straight
 *
 * Joystick UP           → { c:'F', t:'D' }
 * Joystick UP-LEFT      → { c:'F', t:'L' }
 * Joystick LEFT only    → { c:'S', t:'L' }  1s delay
 * Joystick DOWN-RIGHT   → { c:'B', t:'R' }
 * Joystick centred      → { c:'S', t:'D' }  immediate STOP
 */
function deriveCmd(x, y) {
    if (Math.abs(x) <= DEADZONE && Math.abs(y) <= DEADZONE) return { c: 'S', t: 'D' };
    let c = 'S', t = 'D';
    if (Math.abs(y) > DEADZONE) {
        c = y < 0 ? 'F' : 'B';
    }
    if (Math.abs(x) > 0.3) {
        t = x < 0 ? 'L' : 'R';
    }
    return { c, t };
}

// Battery colour: green (good) / amber (medium) / RED (critical, < 30%)
// Hex values on purpose: react-battery-gauge draws SVG, so CSS variables are unreliable.
const BATT_GOOD = '#1fa64a';
const BATT_MID = '#f0b400';
const BATT_CRIT = '#e5173f';
function battColor(p) {
    if (p >= 60) return BATT_GOOD;
    if (p >= 30) return BATT_MID;
    return BATT_CRIT;
}

// Robot telemetry example: {"FID":"6","Cmd":"S","Spd":"120","Turn":"D","GC":1,"SP":1,"SO":0,"MB":50,"CB":70}
// MB = Motor battery %, CB = Controller battery %. Accepts 50, "50" or "50%"; clamps to 0-100.
function toPct(v) {
    const n = parseFloat(v);
    return Number.isFinite(n) ? Math.max(0, Math.min(100, n)) : 0;
}

// Restored: clock hook (was accidentally removed in the previous edit)
function useClock() {
    const [t, setT] = useState('');
    useEffect(() => {
        const fmt = () => new Date().toLocaleTimeString('en-GB', { hour12: false });
        setT(fmt());
        const id = setInterval(() => setT(fmt()), 1000);
        return () => clearInterval(id);
    }, []);
    return t;
}

/*
 * ── TELEMETRY (FROM ROBOT) field reference ──
 *   CMD  : current command      F = forward, B = backward, S = stop
 *   SPD  : speed reported by robot (0–255)
 *   TURN : L = left, R = right, D = straight
 *   FID  : robot / field ID
 *
 * ── TX PAYLOAD (UI -> ROBOT) field reference ──
 *   FID   : robot / field ID
 *   Cmd   : F forward, B backward, S stop  (never L/R — turning uses S + Turn)
 *   Spd   : speed 0–255
 *   Turn  : L left, R right, D straight
 *   GC    : Grass Cutter flag (1 = on)
 *   SP    : Sprayer flag      (1 = on)
 *   SO    : Sowing flag       (1 = on)
 *   MB/CB : Motor / Controller battery % (filled in by the robot)
 */

// ── Time since the robot connected (robot answered the handshake with FID + IP) ──
function LinkTimer({ since }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);
    const sec = Math.max(0, Math.floor((now - since) / 1000));
    const hh = String(Math.floor(sec / 3600)).padStart(2, '0');
    const mm = String(Math.floor((sec % 3600) / 60)).padStart(2, '0');
    const ss = String(sec % 60).padStart(2, '0');
    return <span>{`${hh}:${mm}:${ss}`}</span>;
}

// ── SPRAYER TANK LEVEL ─ tank with animated water, ticks at 25/50/75 %, colour by level ──
//    blue >= 40 %   amber 20-39 %   red < 20 % (REFILL)   grey = not reported yet
function TankGauge({ level }) {
    const known = level !== null && level !== undefined && Number.isFinite(Number(level));
    const pct = known ? Math.max(0, Math.min(100, Number(level))) : 0;
    const color = !known ? '#9aa5af' : pct < 20 ? '#e5173f' : pct < 40 ? '#f0b400' : '#1e88e5';
    const top = 12 + 80 * (1 - pct / 100);               // y of the water surface
    return (
        <div className={s.tankTile}>
            <svg className={s.tankSvg} viewBox="0 0 72 106" role="img"
                aria-label={known ? `Sprayer tank ${Math.round(pct)} percent` : 'Sprayer tank level not reported'}>
                <defs>
                    <clipPath id="rcTankClip"><rect x="10" y="12" width="52" height="80" rx="12" /></clipPath>
                </defs>
                <rect x="28" y="3" width="16" height="9" rx="2.5" fill="#455a64" />
                <rect x="10" y="12" width="52" height="80" rx="12" fill="#f1f5f8" />
                <g clipPath="url(#rcTankClip)">
                    {known && pct > 0 && (
                        <>
                            <rect x="0" y={top + 4} width="72" height={Math.max(0, 92 - top)} fill={color} opacity="0.95" />
                            <g transform={`translate(0 ${top - 2})`}>
                                <path className={s.tankWave} fill={color}
                                    d="M-36 6 Q-27 0 -18 6 T0 6 T18 6 T36 6 T54 6 T72 6 T90 6 V14 H-36 Z" />
                            </g>
                        </>
                    )}
                    {[25, 50, 75].map(t => (
                        <line key={t} x1="46" x2="62" y1={12 + 80 * (1 - t / 100)} y2={12 + 80 * (1 - t / 100)}
                            stroke="#ffffff" strokeOpacity="0.75" strokeWidth="1.5" />
                    ))}
                </g>
                <rect x="10" y="12" width="52" height="80" rx="12" fill="none" stroke="#455a64" strokeWidth="3" />
            </svg>
            <div className={s.tankInfo}>
                <div className={s.tileLabel}>SPRAYER TANK</div>
                <div className={s.tankPct} style={{ color: known ? color : undefined }}>{known ? `${Math.round(pct)}%` : '--'}</div>
                {known && pct < 20 && <div className={s.tankLow}>⚠ REFILL</div>}
            </div>
        </div>
    );
}

// ── POWER LAMP ─ tower lamp, same meaning as the Robot Operation page:
//    red = 0 fault · amber = 1 connected / ready · green = 2 moving · all dim = offline
const LAMP_TEXT = { off: 'OFFLINE', 0: 'FAULT', 1: 'READY', 2: 'RUNNING' };
const LAMP_INK = { off: '#78909c', 0: '#c62828', 1: '#b27a00', 2: '#4a7a0c' };
function PowerLamp({ state }) {
    const key = state === null || state === undefined ? 'off' : state;
    const lamps = [
        { id: 0, y: 8, on: '#f44336', dim: '#f4d2cf', anim: s.lampBlink },
        { id: 1, y: 36, on: '#ffc107', dim: '#f3e6bd', anim: '' },
        { id: 2, y: 64, on: '#99cc33', dim: '#dbe8bc', anim: s.lampPulse },
    ];
    return (
        <div className={s.lampTile}>
            <svg className={s.lampSvg} viewBox="0 0 72 106" role="img" aria-label={`Power lamp ${LAMP_TEXT[key]}`}>
                <defs>
                    <filter id="rcLampGlow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="4" /></filter>
                </defs>
                <rect x="22" y="2" width="28" height="6" rx="2.5" fill="#455a64" />
                <rect x="33" y="90" width="6" height="8" fill="#78909c" />
                <rect x="20" y="97" width="32" height="8" rx="3" fill="#455a64" />
                {lamps.map(l => {
                    const lit = key === l.id;
                    return (
                        <g key={l.id} className={lit ? l.anim : ''}>
                            {lit && <rect x="14" y={l.y - 2} width="44" height="28" rx="10" fill={l.on} filter="url(#rcLampGlow)" opacity="0.8" />}
                            <rect x="16" y={l.y} width="40" height="24" rx="8" fill={lit ? l.on : l.dim}
                                stroke={lit ? l.on : '#cfd8dc'} strokeWidth="1.5" />
                            {lit && <rect x="22" y={l.y + 4} width="14" height="5" rx="2.5" fill="#ffffff" opacity="0.55" />}
                        </g>
                    );
                })}
            </svg>
            <div className={s.lampInfo}>
                <div className={s.tileLabel}>POWER LAMP</div>
                <div className={s.lampText} style={{ color: LAMP_INK[key] }}>{LAMP_TEXT[key]}</div>
            </div>
        </div>
    );
}

// Plant id for the robot frames: same value the Robot Operation page sends as PID
function getPlantId() {
    const fromSession = sessionStorage.getItem('user_plantid');
    if (fromSession) return fromSession;
    const m = /[?&]pid=([^&]+)/.exec(window.location.hash || window.location.search || '');
    return m ? decodeURIComponent(m[1]) : null;
}

// Action button (Scan Farm / Charging / Parking / Off / Return). Defined OUTSIDE the page component so it is not re-mounted on every render.
//   same look as the Robot Operation page: neutral by default, filled with its own colour when selected
function ActionBtn({ icon, label, variant, onClick, active, wide }) {
    return (
        <button type="button" onClick={onClick} title={label}
            aria-pressed={active === undefined ? undefined : !!active}
            className={`${s.actBtn} ${s['act_' + variant]}${active ? ' ' + s.act_on : ''}${wide ? ' ' + s.act_wide : ''}`}>
            <i className={`fa ${icon}`} aria-hidden="true"></i>
            <span>{label}</span>
        </button>
    );
}

export default function RobotControl() {
    const { status, logs, robotState, cameraIP, connectedAt, connect, disconnect, publish, clearLogs } = useMQTT();

    const [broker] = useState('c7cd69d47ed34850b8238e53f74566f2.s1.eu.hivemq.cloud');
    const [port] = useState('8884');
    const [mqttUser] = useState('hivemq.webclient.1741693400426');
    const [mqttPass] = useState('9L%7,vs!48zAEPihBkD@');
    const [topic] = useState('zylv4u8fun1');
    const [teleTopic] = useState('e1ypuwron21');
    var rbtID = sessionStorage.getItem("rbtID");
    //alert(rbtID);
    const [fid, setFid] = useState(rbtID);
    const [uiSpeed, setUiSpeed] = useState(120);
    const [uiFlags, setUiFlags] = useState({ GC: 0, SP: 0, SO: 0 });
    const [selectedAction, setSelectedAction] = useState('');   // Charging / Parking / Off stays highlighted until the robot is driven again

    // The Speed slider and the FLAGS (UI -> ROBOT) now FOLLOW the telemetry coming back from the robot,
    // the same way the TELEMETRY panel does. A change the user just made is never overwritten for a short moment.
    const speedTouchedRef = useRef(0);
    const flagsTouchedRef = useRef(0);
    const changeSpeed = useCallback((v) => { speedTouchedRef.current = Date.now(); setUiSpeed(v); }, []);

    // Speed: follow the speed the robot reports while it is moving (Spd > 0).
    // Spd = 0 is the robot at rest after a STOP, so the chosen speed stays on the slider.
    useEffect(() => {
        const spd = parseInt(robotState.Spd, 10);
        if (Number.isFinite(spd) && spd > 0 && Date.now() - speedTouchedRef.current > 1500) {
            setUiSpeed(Math.min(255, spd));
        }
    }, [robotState.Spd]);

    // Flags GC / SP / SO: follow the flags the robot reports (also when they were changed from another page)
    useEffect(() => {
        if (Date.now() - flagsTouchedRef.current < 2000) return;
        setUiFlags({ GC: robotState.GC ? 1 : 0, SP: robotState.SP ? 1 : 0, SO: robotState.SO ? 1 : 0 });
    }, [robotState.GC, robotState.SP, robotState.SO]);

    const joyRef = useRef({ x: 0, y: 0 });
    const timerRef = useRef(null);   // F/B 50ms throttle
    const lrTimerRef = useRef(null);   // L/R 1s one-shot (never reset while held)
    const clock = useClock();
    const navigate = useNavigate();

    const mkPayload = useCallback((c, t, spd, fl = uiFlags) => ({
        FID: String(fid), Cmd: c, Spd: String(spd), Turn: t,
        GC: fl.GC, SP: fl.SP, SO: fl.SO, MB: 0, CB: 0,
    }), [fid, uiFlags]);

    const sendCmd = useCallback((c, t, spd, fl) => {
        if (c !== 'S') setSelectedAction('');   // the robot is being driven -> no action button stays highlighted
        publish(topic, mkPayload(c, t, spd, fl));
    }, [mkPayload, publish, topic]);

    /*
     * STOP                      → instant, cancels BOTH timers
     * F/B (any turn)            → 50ms throttle   (timerRef)
     * Turn-in-place c=S t=L/R  → 1s ONE-SHOT      (lrTimerRef)
     *
     * ROOT CAUSE OF OLD BUG:
     * The joystick fires onMove continuously while held (~every 16ms).
     * Old code reset the 1s timer on every call → timer NEVER fired.
     * Fix: only START lrTimerRef if it is not already running.
     */
    /*
     * ─── handleJoyMove timing rules ───────────────────────────
     *
     *  STOP  (x=0, y=0)      → instant, cancels both timers
     *  F / B  (any x)        → 200ms throttle — fires once, waits 200ms, repeats
     *  Turn-in-place (c=S)   → 1s initial delay, then 1.5s keep-alive while held
     *
     * Why 200ms for F/B?
     *   Log showed F firing every 50ms = 20 commands/second → floods MQTT.
     *   200ms = 5 commands/second → robot stays responsive without flooding.
     *
     * Why lrTimerRef one-shot then restarts?
     *   After lrTimer fires → lrTimerRef=null → next joystick update (16ms)
     *   starts a new 1.5s timer → fires again → robot keeps turning while held.
     *   This is correct: robot needs periodic commands to keep moving.
     * ────────────────────────────────────────────────────────── */
    // NOTE: L/R (turn-in-place) is delayed via lrTimerRef; F/B is sent immediately / throttled via timerRef.
    const handleJoyMove = useCallback(({ x, y }) => {
        joyRef.current = { x, y };

        // ── STOP: instant, cancel both timers ──
        if (x === 0 && y === 0) {
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            if (lrTimerRef.current) { clearTimeout(lrTimerRef.current); lrTimerRef.current = null; }
            sendCmd('S', 'D', 0);
            return;
        }

        const { c, t } = deriveCmd(x, y);

        // Turn-in-place: c='S' with t='L' or t='R'
        const isTurnInPlace = c === 'S' && (t === 'L' || t === 'R');

        if (isTurnInPlace) {
            // Cancel F/B timer
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            // One-shot: start only if not already pending
            // After it fires (sets null), the next joystick update restarts with 1500ms
            // giving a 1s initial delay then 1.5s keep-alive repeat while joystick held
            if (!lrTimerRef.current) {
                const delay = lrTimerRef._fired ? 1000 : 500; // 1s first, 1.5s repeats
                lrTimerRef.current = setTimeout(() => {
                    lrTimerRef.current = null;
                    lrTimerRef._fired = true;
                    const snap = deriveCmd(joyRef.current.x, joyRef.current.y);
                    // Only send if still in turn-in-place
                    if (snap.c === 'S' && (snap.t === 'L' || snap.t === 'R')) {
                        sendCmd(snap.c, snap.t, uiSpeed);
                    }
                }, delay);
            }
        } else {
            // F / B — cancel L/R timer, reset fired flag, 200ms throttle
            if (lrTimerRef.current) { clearTimeout(lrTimerRef.current); lrTimerRef.current = null; }
            lrTimerRef._fired = false;
            if (timerRef.current) return;
            timerRef.current = setTimeout(() => {
                timerRef.current = null;
                const snap = deriveCmd(joyRef.current.x, joyRef.current.y);
                sendCmd(snap.c, snap.t, uiSpeed);
            }, 200);
        }
    }, [sendCmd, uiSpeed]);

    /*
     * D-Pad:
     *   F/B → Cmd F/B Turn D   immediate
     *   L/R → Cmd S  Turn L/R  800ms one-shot delay
     *   S   → immediate STOP
     */
    const DMAP = {
        F: { c: 'F', t: 'D' },
        B: { c: 'B', t: 'D' },
        L: { c: 'S', t: 'L' },
        R: { c: 'S', t: 'R' },
        S: { c: 'S', t: 'D' },
    };

    const onDpadPress = useCallback(k => {
        const { c, t } = DMAP[k];
        const isTurnInPlace = c === 'S' && (t === 'L' || t === 'R');
        if (isTurnInPlace) {
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            if (!lrTimerRef.current) {
                lrTimerRef.current = setTimeout(() => {
                    lrTimerRef.current = null;
                    sendCmd(c, t, uiSpeed);
                }, 800);
            }
        } else {
            if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
            if (lrTimerRef.current) { clearTimeout(lrTimerRef.current); lrTimerRef.current = null; }
            sendCmd(c, t, uiSpeed);
        }
    }, [sendCmd, uiSpeed]);

    const onDpadRelease = useCallback(() => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        if (lrTimerRef.current) { clearTimeout(lrTimerRef.current); lrTimerRef.current = null; }
        sendCmd('S', 'D', 0);
    }, [sendCmd]);

    const toggleFlag = key => {
        flagsTouchedRef.current = Date.now();
        setUiFlags(prev => {
            const next = { ...prev, [key]: prev[key] ? 0 : 1 };
            sendCmd(robotState.Cmd, robotState.Turn, uiSpeed, next);
            return next;
        });
    };

    // Power lamp: null = offline · 0 = fault (error frame) · 1 = connected / ready · 2 = moving   (same as Robot Operation)
    const MOVING_CMDS = ['F', 'B', 'FR', 'FL', 'BR', 'BL'];
    const lampState = (status !== 'online' || robotState.FID === null) ? null
        : robotState.ERR ? 0
            : (MOVING_CMDS.includes(robotState.Cmd) || robotState.Turn === 'L' || robotState.Turn === 'R') ? 2
                : 1;

    const statusColor = status === 'online' ? 'var(--accent3)' : status === 'connecting' ? 'var(--warn)' : 'var(--accent4)';

    // Motor battery (MB) and Controller battery (CB) – both use the battery icon
    const battBars = [
        { key: 'MB', label: 'MOTOR' },
        { key: 'CB', label: 'CONTROLLER' },
    ];
    const flagDefs = [
        { key: 'GC', label: 'GRASS CUTTER' },
        { key: 'SP', label: 'SPRAYER' },
        { key: 'SO', label: 'SOWING' },
    ];
    // ── Scan Farm / Charging / Parking — same commands as the Robot Operation page ──
    //   Charging : Cmd "RTCS"   Parking : Cmd "RTP"
    //   frame    : { PID, FID, Cmd, Turn:"D", Spd:255, GC:0, SP:0, SO:0, CB:0, MB:0, TL:0 }  (flags are forced to 0)
    //   Scan Farm: opens the Agri dashboard ( navigate('/agri-dashboard') )
    const cancelPendingDrive = () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        if (lrTimerRef.current) { clearTimeout(lrTimerRef.current); lrTimerRef.current = null; }
    };

    const sendReturnCmd = (cmd) => {
        cancelPendingDrive();               // a late joystick F/B must not override Charging / Parking
        const frame = {};
        const pid = getPlantId();
        if (pid !== null) frame.PID = pid;
        Object.assign(frame, {
            FID: String(fid), Cmd: cmd, Turn: 'D', Spd: 255,
            GC: 0, SP: 0, SO: 0, CB: 0, MB: 0, TL: 0,
        });
        publish(topic, frame);
        flagsTouchedRef.current = Date.now();
        setUiFlags({ GC: 0, SP: 0, SO: 0 }); // the robot gets all flags = 0, so show them OFF
    };
    const onCharging = () => { sendReturnCmd('RTCS'); setSelectedAction('Charging'); };
    const onParking = () => { sendReturnCmd('RTP'); setSelectedAction('Parking'); };

    // OFF — same frame as the Robot Operation page: { PID, FID, OPN: "OFF" }.
    // The flags go OFF, and (like Robot Operation, which also stops the camera) this page's link is closed a moment later.
    const onOff = () => {
        cancelPendingDrive();
        const frame = {};
        const pid = getPlantId();
        if (pid !== null) frame.PID = pid;
        Object.assign(frame, { FID: String(fid), OPN: 'OFF' });
        publish(topic, frame);
        flagsTouchedRef.current = Date.now();
        setUiFlags({ GC: 0, SP: 0, SO: 0 });
        setSelectedAction('Off');
        if (status !== 'offline') setTimeout(disconnect, 300);
    };

    // RETURN TO ROBOT OPERATION — back to the multi-robot page (/robot-operation?pid=...), the robot is stopped first
    const onReturnToOperation = () => {
        cancelPendingDrive();
        if (status === 'online') sendCmd('S', 'D', 0);
        const pid = getPlantId();
        navigate(pid ? `/robot-operation?pid=${pid}` : '/robot-operation');
        if (status !== 'offline') setTimeout(disconnect, 300);
    };

    const onScanFarm = () => {
        cancelPendingDrive();
        if (status === 'online') sendCmd('S', 'D', 0);   // never leave the robot moving when this page is closed
        navigate('/agri-dashboard');
        if (status !== 'offline') setTimeout(disconnect, 300); // close this page's MQTT link after the Stop has gone out
    };


    const pick = (command, handler, robotID) => {
        return () => {
            //setSelectedCmd(command);
            handler(robotID);
        };
    };
    return (
        <div className={s.root}>

            {/* TOP BAR */}
            <div className={s.topBar}>
                <div className={s.statusPill}>
                    <div className={`${s.dot} ${s['dot_' + status]}`} />
                    <span className={s.statusText} style={{ color: statusColor }}>
                        {status.toUpperCase()}
                    </span>
                    {robotState.FID !== null && (
                        <span className={s.fidBadge}>FID {robotState.FID}</span>
                    )}
                    {connectedAt && (
                        <span className={s.linkTime} title="Time since the robot connected">
                            <span className={s.linkLbl}>LINK</span> <LinkTimer since={connectedAt} />
                        </span>
                    )}
                    <span className={s.clock}>{clock}</span>
                </div>
                <div className={s.connFields}>
                    <span className={s.lbl}>FID</span>
                    <input className={`${s.inp} ${s.inpXs}`} value={fid}
                        onChange={e => setFid(String(e.target.value) || '0')} />
                </div>
                {status !== 'online'
                    ? <button className={`${s.connBtn} ${s.btnConn}`}
                        disabled={status === 'connecting'}
                        onClick={() => connect(broker, port, mqttUser, mqttPass, topic, teleTopic, fid)}>
                        {status === 'connecting' ? '⟳ CONNECTING…' : '▶ CONNECT'}
                    </button>
                    : <button className={`${s.connBtn} ${s.btnDisc}`} onClick={disconnect}>
                        ■ DISCONNECT
                    </button>
                }
            </div>

            {/* BODY — desktop: 3 columns [controls | camera+dpad+log | joystick+speed]
                tablet: 2 columns · mobile: 1 column in this order:
                1 Camera · 2 Joystick · 3 Directional Pad · 4 Emergency Stop
                5 Flags (UI → Robot) · 6 Speed · 7 Battery · 8 Telemetry · 9 Command Log */}
            <div className={s.body}>

                {/* LEFT COL — Emergency Stop, Battery from Robot, Telemetry from Robot */}
                <div className={s.colControls}>
                    <button className={`${s.estop} ${s.oEstop}`} onClick={() => sendCmd('S', 'D', 0)}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                            <circle cx="9" cy="9" r="7.5" stroke="white" strokeWidth="1.5" />
                            <rect x="5" y="5" width="8" height="8" fill="white" rx="1.5" />
                        </svg>
                        EMERGENCY STOP
                    </button>

                    <div className={`${s.panel} ${s.oBatt}`}>
                        <div className={s.pTitle}>🔋 BATTERY <span className={s.pSub}>FROM ROBOT</span></div>
                        {battBars.map(({ key, label }) => {
                            const pct = toPct(robotState?.[key]);
                            const col = battColor(pct);
                            return (
                                <div key={key} className={s.battRow}>
                                    <div className={s.battHead}>
                                        <span className={s.battLabel}>{label}</span>
                                        {pct < 20 && <span className={s.battLowTxt}>⚠ LOW</span>}
                                        {/* react-battery-gauge: fills to pct%, turns RED when critical */}
                                        <div className={s.battGauge}>
                                            <BatteryGauge
                                                value={pct}
                                                size={110}
                                                aspectRatio={0.5}
                                                padding={2}
                                                animated={false} // instant update (animation made changes look delayed)
                                                customization={{
                                                    batteryBody: { fill: '#ffffff', strokeColor: '#1f2933', strokeWidth: 3, cornerRadius: 5 },
                                                    batteryCap: { fill: '#1f2933', strokeColor: '#1f2933', strokeWidth: 2, cornerRadius: 2, capToBodyRatio: 0.35 },
                                                    batteryMeter: { fill: col, lowBatteryValue: 20, lowBatteryFill: BATT_CRIT, outerGap: 2, noOfCells: 1, interCellsGap: 1 },
                                                    readingText: { lightContrastColor: '#1f2933', darkContrastColor: '#ffffff', lowBatteryColor: BATT_CRIT, fontFamily: 'Orbitron, sans-serif', fontSize: 16, showPercentage: true },
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* SPRAYER TANK LEVEL + POWER LAMP (from the robot telemetry: TL, Cmd/Turn, error frames) */}
                    <div className={`${s.panel} ${s.oStatus}`}>
                        <div className={s.pTitle}>◈ TANK &amp; LAMP <span className={s.pSub}>FROM ROBOT</span></div>
                        <div className={s.statusRow}>
                            <TankGauge level={robotState.TL} />
                            <PowerLamp state={lampState} />
                        </div>
                    </div>

                    <div className={`${s.panel} ${s.oTele}`}>
                        <div className={s.pTitle}>◈ TELEMETRY <span className={s.pSub}>FROM ROBOT</span></div>
                        {/* One compact panel for everything reported by the robot
                            (replaces the old ROBOT STATE and ROBOT FLAGS blocks) */}
                        <div className={s.telemGrid}>
                            {[
                                { l: 'CMD', v: robotState.Cmd, c: robotState.Cmd === 'F' ? '#1fa64a' : robotState.Cmd === 'B' ? '#e5173f' : '#1f2933' },
                                { l: 'SPD', v: robotState.Spd, c: '#0a6ea8' },
                                { l: 'TURN', v: robotState.Turn, c: '#b37a00' },
                                { l: 'FID', v: robotState.FID ?? fid, c: '#d9600a' },
                            ].map(({ l, v, c }) => (
                                <div key={l} className={s.tBox}>
                                    <div className={s.tLbl}>{l}</div>
                                    <div className={s.tVal} style={{ color: c }}>{v ?? '—'}</div>
                                </div>
                            ))}
                        </div>
                        <div className={s.flagChips}>
                            {['GC', 'SP', 'SO'].map(k => (
                                <div key={k} className={`${s.chip} ${robotState[k] ? s.chipOn : ''}`}>
                                    <span className={s.chipKey}>{k}</span>
                                    <span className={s.chipVal}>{robotState[k] ? 'ON' : 'OFF'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                   
                </div>

                {/* CENTER COL — Camera, [Directional Pad | Flags UI → Robot] */}
                <div className={s.colCenter}>
                    <div className={`${s.cameraBlock} ${s.oCamera}`}>
                        <CameraView connected={status === 'online'} />
                    </div>
                    {/* D-Pad + Flags: 2 columns when there is room, 2 lines on small screens */}
                    <div className={s.padSpeedRow}>
                        <div className={`${s.panel} ${s.dpadBlock} ${s.oDpad}`}>
                            <div className={s.pTitle}>◈ DIRECTIONAL PAD</div>
                            <div className={s.dpadRow}>
                                <DPad onPress={onDpadPress} onRelease={onDpadRelease} />
                                {/* TX PAYLOAD preview hidden on request.
                                    To restore: <PayloadPreview payload={curPayload} /> */}
                            </div>
                        </div>
                        <div className={`${s.panel} ${s.oFlags}`}>
                            <div className={s.pTitle}>⊞ FLAGS <span className={s.pSub}>UI → ROBOT</span></div>
                            <div className={s.flagGrid}>
                                {flagDefs.map(({ key, label }) => (
                                    <button key={key}
                                        className={`${s.flagBtn} ${uiFlags[key] ? s.flagOn : ''}`}
                                        onClick={() => toggleFlag(key)}>
                                        <div className={`${s.led} ${uiFlags[key] ? s.ledOn : ''}`} />
                                        <span className={s.flagKey}>{key}</span>
                                        <span className={s.flagSub}>{label}</span>
                                        <span className={s.flagState}
                                            style={{ color: uiFlags[key] ? '#ffffff' : '#333333' }}>
                                            {uiFlags[key] ? '■ ON' : '□ OFF'}
                                        </span>
                                    </button>
                                ))}
                                <button className={s.sendBtn}
                                    onClick={() => sendCmd(robotState.Cmd, robotState.Turn, uiSpeed)}>
                                    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                                        <path d="M1 7L13 1 9.5 7 13 13 1 7Z" fill="currentColor" />
                                    </svg>
                                    <span className={s.flagKey}>SEND</span>
                                    <span className={s.flagSub}>MANUAL CMD</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COL — Joystick, Speed */}
                <div className={s.colJoystick}>
                    <div className={`${s.panel} ${s.panelJoy} ${s.oJoy}`}>
                        <div className={s.pTitle}>⊕ JOYSTICK <span className={s.pSub}>RIGHT HAND</span></div>
                        <div className={s.joyLayout}>
                            <VirtualJoystick onMove={handleJoyMove} />
                        </div>
                    </div>

                    {/* SPEED CONTROLLER — right side, under the joystick */}
                    <div className={`${s.panel} ${s.oSpeed}`}>
                        <SpeedSlider speed={uiSpeed} onChange={changeSpeed} />
                    </div>
                    <div className={`${s.panel} ${s.oActions}`}>
                        <div className={s.pTitle}>◈ ACTIONS</div>
                        <div className={s.actRow}>
                            <ActionBtn variant="blue" icon="fa-search" label="Scan Farm" onClick={onScanFarm} />
                            <ActionBtn variant="amber" icon="fa-bolt" label="Charging" active={selectedAction === 'Charging'} onClick={onCharging} />
                            <ActionBtn variant="slate" icon="fa-taxi" label="Parking" active={selectedAction === 'Parking'} onClick={onParking} />
                            <ActionBtn variant="dark" icon="fa-power-off" label="Off" active={selectedAction === 'Off'} onClick={onOff} />
                            <ActionBtn wide variant="blue" icon="fa-arrow-left" label="Return to Robot Operation" onClick={onReturnToOperation} />
                        </div>
                    </div>
                </div>


                {/* 9. COMMAND LOG — full-width row along the bottom (single column) */}
                <div className={`${s.panel} ${s.logPanel} ${s.oLog} ${s.logRow}`}>
                    <CommandLog logs={logs} onClear={clearLogs} />
                </div>

            </div>
        </div>
    );
}